<template>
  <div class="p-6">
    <h2 class="mb-4">Level System Manager</h2>

    <!-- Registry Management Section -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold mb-4">Level System Registry</h3>
      <div class="mb-4 flex space-x-3">
        <Button label="Initialize Registry" icon="pi pi-database" @click="initializeRegistry"
          :loading="loading.registry" />
        <Button label="Add Registry Entry" icon="pi pi-plus" @click="openCreateRegistryDialog" />
      </div>

      <div class="bg-gray-50 p-4 rounded-lg">
        <h4 class="font-medium mb-3">Registry Entries</h4>
        <div v-if="registryEntries.length === 0" class="text-gray-500">
          No registry entries found. Click "Initialize Registry" to create default entries.
        </div>
        <div v-else class="space-y-3">
          <div v-for="entry in registryEntries" :key="entry._id"
           class="bg-white p-3 rounded border">
            <div class="flex justify-between items-start">
              <div>
                <div class="font-medium">{{ entry.label }}</div>
                <div class="text-sm text-gray-600">{{ entry.description }}</div>
                <div class="text-xs text-gray-500 mt-1">
                  Type: {{ entry.type }} | Development points: {{ entry.xpPerCompletion }}
                </div>
              </div>
              <div class="flex space-x-2">
                <Button icon="pi pi-pencil" size="small" @click="editRegistryEntry(entry)" />
                <Button icon="pi pi-trash" size="small" severity="danger"
                 @click="showDeleteRegistryDialog(entry)"
                 :disabled="deleteRegistryDialog.visible" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Level System Configuration Section -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold mb-4">Level System Configuration</h3>
      <div class="bg-gray-50 p-4 rounded-lg">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div class="flex flex-col">
            <label for="inactivityThreshold" class="text-sm font-medium text-gray-700 mb-1">
              Inactivity threshold (days)
            </label>
            <InputNumber
              id="inactivityThreshold"
              v-model="levelSystemConfig.inactivityThresholdDays"
              :min="1"
              :max="365"
              show-buttons
              class="w-full"
            />
          </div>
          <div class="flex flex-col">
            <label class="text-sm font-medium text-gray-700 mb-1">
              Notification channels
            </label>
            <MultiSelect
              v-model="levelSystemConfig.inactivityNotificationChannels"
              :options="levelSystemChannelOptions"
              option-label="label"
              option-value="value"
              placeholder="Select channels"
              class="w-full"
            />
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-4 mb-4">
          <div class="flex items-center gap-2">
            <ToggleSwitch v-model="levelSystemConfig.stabilisationReminderEnabled" />
            <label class="text-sm font-medium text-gray-700">Stabilisation reminder enabled</label>
          </div>
          <div
           v-if="levelSystemConfig.stabilisationReminderEnabled"
           class="flex items-center gap-2">
            <label class="text-sm font-medium text-gray-700">Interval (days)</label>
            <InputNumber
              v-model="levelSystemConfig.stabilisationReminderIntervalDays"
              :min="1"
              :max="90"
              show-buttons
              class="w-32"
            />
          </div>
        </div>
        <Button
          label="Save"
          icon="pi pi-check"
          @click="saveLevelSystemConfig"
          :loading="loadingLevelSystemConfig"
        />
        <div v-if="levelSystemConfigMessage" class="mt-3">
          <div
           v-if="levelSystemConfigSuccess"
           class="bg-green-50 border border-green-200 rounded-lg p-3 text-green-800">
            {{ levelSystemConfigMessage }}
          </div>
          <div v-else class="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800">
            {{ levelSystemConfigMessage }}
          </div>
        </div>
      </div>
    </div>

    <!-- Level Targets Management Section -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold mb-4">Level Targets</h3>
      <div class="mb-4 flex gap-3">
        <Button label="Create New Level" icon="pi pi-plus" @click="openCreateLevelDialog" />
        <Button
          label="Anomalies Detector"
          icon="pi pi-exclamation-triangle"
          severity="warning"
          @click="anomaliesDetectorVisible = true"
        />
      </div>

      <div class="bg-gray-50 p-4 rounded-lg">
        <h4 class="font-medium mb-3">Level Definitions</h4>
        <div v-if="levelTargets.length === 0" class="text-gray-500">
          No level targets defined. Create your first level to get started.
        </div>
        <div v-else class="space-y-3">
          <div v-for="level in levelTargets" :key="level._id" class="bg-white p-3 rounded border">
            <div class="flex justify-between items-start">
              <div>
                <div class="font-medium">Level {{ level.level }}: {{ level.title }}</div>
                <div class="text-sm text-gray-600">
                  Total development points: {{ level.totalXP }}</div>
                <div class="text-xs text-gray-500 mt-1">
                  {{ level.goals.length }} goals defined
                </div>
              </div>
              <div class="flex space-x-2">
                <Button icon="pi pi-eye" size="small" @click="viewLevelDetails(level)" />
                <Button icon="pi pi-pencil" size="small" @click="editLevel(level)" />
                <Button icon="pi pi-trash" size="small" severity="danger"
                 @click="showDeleteLevelDialog(level)"
                 :disabled="deleteLevelDialog.visible" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- User Progress Overview Section -->
    <div class="mb-8">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-semibold">User Progress Overview</h3>
        <Button icon="pi pi-refresh" size="small" @click="refreshUserStats" :loading="loading.users"
          label="Refresh Stats" />
      </div>

      <div class="bg-gray-50 p-6 rounded-lg">
        <!-- Main Stats Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div class="bg-white p-4 rounded-lg border shadow-sm text-center">
            <div class="text-3xl font-bold text-blue-600 mb-1">{{ userStats.totalUsers || 0 }}</div>
            <div class="text-sm text-gray-600">Total Users</div>
            <div class="text-xs text-gray-500 mt-1">Registered in system</div>
          </div>

          <div class="bg-white p-4 rounded-lg border shadow-sm text-center">
            <div class="text-3xl font-bold text-green-600 mb-1">{{
               userStats.activeUsers || 0 }}</div>
            <div class="text-sm text-gray-600">Active Users</div>
            <div class="text-xs text-gray-500 mt-1">Last 30 days</div>
          </div>

          <div class="bg-white p-4 rounded-lg border shadow-sm text-center">
            <div class="text-3xl font-bold text-purple-600 mb-1">{{ userStats.avgLevel || 0 }}</div>
            <div class="text-sm text-gray-600">Average Level</div>
            <div class="text-xs text-gray-500 mt-1">Across all users</div>
          </div>

          <div class="bg-white p-4 rounded-lg border shadow-sm text-center">
            <div class="text-3xl font-bold text-orange-600 mb-1">{{ userStats.totalXP || 0 }}</div>
            <div class="text-sm text-gray-600">Total development points earned</div>
            <div class="text-xs text-gray-500 mt-1">System-wide</div>
          </div>
        </div>

              <!-- Level Distribution Chart -->
      <div class="bg-white p-4 rounded-lg border shadow-sm mb-6">
        <h4 class="font-medium text-gray-700 mb-3">Level Distribution</h4>
        <div v-if="userStats.levelDistribution && userStats.levelDistribution.length > 0"
             class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <div v-for="level in userStats.levelDistribution" :key="level.level"
               class="text-center p-3 bg-gray-50 rounded border">
            <div class="text-lg font-bold text-gray-800">{{ level.level }}</div>
            <div class="text-sm text-gray-600">{{ level.userCount }} users</div>
            <div class="text-xs text-gray-500">{{ level.percentage }}%</div>
          </div>
        </div>
        <div v-else class="text-center text-gray-500 py-4">
          <div class="text-sm text-gray-400 mb-2">No level distribution data available yet</div>
          <div class="text-xs text-gray-400">
            Users will appear here once they start using the level system
          </div>
        </div>
      </div>

                <!-- Recent Activity -->
        <div class="bg-white p-4 rounded-lg border shadow-sm">
          <h4 class="font-medium text-gray-700 mb-3">Recent Level Progress</h4>
          <div v-if="userStats.recentProgress && userStats.recentProgress.length > 0"
               class="space-y-2 max-h-40 overflow-y-auto">
            <div v-for="progress in userStats.recentProgress" :key="progress.id"
                 class="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
              <span class="text-gray-700">{{ progress.userName }}</span>
              <span class="text-gray-600">Level {{ progress.level }}</span>
              <span class="text-gray-500">{{ progress.action }}</span>
              <span class="text-xs text-gray-400">{{ formatTimeAgo(progress.timestamp) }}</span>
            </div>
          </div>
          <div v-else class="text-center text-gray-500 py-4">
            <div class="text-sm text-gray-400 mb-2">No recent activity yet</div>
            <div class="text-xs text-gray-400">
              User progress will appear here as they complete goals
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- User Level Assignment Section -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold mb-4">Assign All Levels to User</h3>

      <!-- Warning Message -->
      <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <div class="flex items-center">
          <i class="pi pi-exclamation-triangle text-red-600 text-xl mr-3"></i>
          <div>
            <h4 class="font-semibold text-red-800">⚠️ Important Warning</h4>
            <p class="text-red-700 mt-1">
              This feature should only be used if a user is missing the level system entirely.
              All current levels will be assigned to the user
              - this will not work for individual level assignments.
            </p>
          </div>
        </div>
      </div>

      <div class="bg-gray-50 p-6 rounded-lg">
        <div class="flex items-end space-x-4">
          <div class="flex-1">
            <label for="userIdInput" class="block text-sm font-medium text-gray-700 mb-2">
              User ID
            </label>
            <input
              id="userIdInput"
              v-model="userAssignment.userId"
              type="text"
              placeholder="Enter user ID"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none
              focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              :disabled="userAssignment.loading"
            />
          </div>
          <Button
            label="Assign All Levels"
            icon="pi pi-user-plus"
            @click="assignAllLevelsToUser"
            :loading="userAssignment.loading"
            :disabled="!userAssignment.userId || userAssignment.loading"
            severity="warning"
          />
        </div>

        <!-- Success/Error Messages -->
        <div v-if="userAssignment.message" class="mt-4">
          <div v-if="userAssignment.success" class="bg-green-50 border border-green-200 rounded-lg
            p-3">
            <div class="flex items-center">
              <i class="pi pi-check-circle text-green-600 mr-2"></i>
              <span class="text-green-800">{{ userAssignment.message }}</span>
            </div>
          </div>
          <div v-else class="bg-red-50 border border-red-200 rounded-lg p-3">
            <div class="flex items-center">
              <i class="pi pi-exclamation-circle text-red-600 mr-2"></i>
              <span class="text-red-800">{{ userAssignment.message }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- User Level History Section -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold mb-4">User Level History</h3>
      <div class="bg-gray-50 p-4 rounded-lg">
        <div class="flex flex-wrap gap-4 items-end mb-4">
          <div class="flex flex-col">
            <label class="text-sm font-medium text-gray-700 mb-1">User Email</label>
            <InputText
              v-model="userHistoryEmail"
              placeholder="Enter user email"
              class="w-64"
            />
          </div>
          <Button
            label="Load History"
            icon="pi pi-search"
            @click="loadUserHistory"
            :loading="levelSystemLoading"
          />
          <div class="flex flex-col">
            <label class="text-sm font-medium text-gray-700 mb-1">Export detail</label>
            <Dropdown
              v-model="userHistoryExportDetailLevel"
              :options="userHistoryExportDetailOptions"
              optionLabel="label"
              optionValue="value"
              class="w-40"
            />
          </div>
          <Button
            label="Export User History CSV"
            icon="pi pi-download"
            severity="secondary"
            @click="exportUserHistoryClick"
            :loading="levelSystemLoading"
          />
          <Button
            label="Recalculate level state"
            icon="pi pi-refresh"
            severity="secondary"
            :disabled="!userHistory || !userHistoryUserId"
            :loading="recalculateLoading"
            @click="recalculateLevelStateClick"
            title="Recalculate and fix level state from goal progress (admin)"
          />
        </div>

        <div v-if="userHistory" class="mb-4">
          <h4 class="font-medium text-gray-700 mb-2">User</h4>
          <div class="flex flex-wrap gap-6 text-sm">
            <span><strong>Name:</strong> {{ userHistory.user?.name }}</span>
            <span><strong>Email:</strong> {{ userHistory.user?.email }}</span>
            <span v-if="userHistory.user?.teams?.length"><strong>Teams:</strong>
               {{ userHistory.user.teams.join(', ') }}</span>
            <span v-if="userHistory.user?.date_registered"><strong>Registered:</strong>
               {{ formatHistoryDate(userHistory.user.date_registered) }}</span>
            <span><strong>Current Level:</strong> {{ userHistory.currentLevel }}</span>
            <span><strong>Total development points:</strong> {{ userHistory.totalXP }}</span>
            <span><strong>Days since last progress:</strong>
               {{ userHistory.daysSinceLastProgress ?? '—' }}</span>
            <span>
              <strong>Inactivity warning:</strong>
               {{
                 userHistory.inactivityWarningSet ? 'Yes' + (userHistory.inactivityWarningAt
                  ? ' (' + formatHistoryDate(userHistory.inactivityWarningAt) + ')' : '') : 'No'
               }}
            </span>
          </div>
          <div v-if="groupAverage" class="mt-2 text-sm text-gray-600">
            <strong>Group comparison:</strong>
            Avg level: {{ groupAverage.avgLevel }} |
            Avg total development points: {{ groupAverage.avgTotalXP }} |
            Avg days per level: {{ groupAverage.avgDaysPerLevel }} |
            Avg completion rate: {{ (groupAverage.avgCompletionRate * 100).toFixed(0) }}%
            <span v-if="groupAverage.avgDeviationDays != null"> | Avg deviation (days):
               {{ groupAverage.avgDeviationDays }}
            </span>
          </div>
        </div>

        <DataTable
          v-if="userHistory && userHistory.levels && userHistory.levels.length > 0"
          :value="userHistory.levels"
          v-model:expandedRows="expandedHistoryRows"
          :loading="levelSystemLoading"
          dataKey="level"
          responsiveLayout="scroll"
          class="p-datatable-sm"
        >
          <Column expander style="width: 3rem" />
          <Column field="level" header="Level" />
          <Column field="title" header="Title" />
          <Column header="Development points">
            <template #body="{ data }">
              {{ data.currentXP }} / {{ data.totalXP }}
            </template>
          </Column>
          <Column field="completed" header="Completed">
            <template #body="{ data }">
              {{ data.completed ? 'Yes' : 'No' }}
            </template>
          </Column>
          <Column header="Started">
            <template #body="{ data }">
              {{ formatHistoryDate(data.startedAt) }}
            </template>
          </Column>
          <Column header="Completed At">
            <template #body="{ data }">
              {{ data.completedAt ? formatHistoryDate(data.completedAt) : '—' }}
            </template>
          </Column>
          <Column header="Duration (days)">
            <template #body="{ data }">
              {{ data.durationDays ?? '—' }}
            </template>
          </Column>
          <Column header="Completion Rate">
            <template #body="{ data }">
              {{ data.completionRate != null ? (data.completionRate * 100).toFixed(0) + '%' : '—' }}
            </template>
          </Column>
          <Column header="Target Date">
            <template #body="{ data }">
              {{ data.estimatedFinishDate ? formatHistoryDate(data.estimatedFinishDate) : '—' }}
            </template>
          </Column>
          <Column header="Deviation">
            <template #body="{ data }">
              <span v-if="data.deviationDays == null">—</span>
              <span v-else-if="data.isDelayed"
               class="text-red-600" :title="data.deviationDays + ' days over target'">
                +{{ data.deviationDays }} d
              </span>
              <span v-else class="text-green-600" :title="data.deviationDays + ' days vs target'">
                {{ data.deviationDays }} d
              </span>
            </template>
          </Column>

          <template #expansion="{ data }">
            <div class="p-3">
              <h5 class="font-semibold text-sm mb-2">Goals for Level {{ data.level }}</h5>
              <table v-if="data.goals && data.goals.length > 0" class="w-full text-sm border-collapse">
                <thead>
                  <tr class="bg-gray-100 text-left">
                    <th class="py-2 px-3 border-b">Type</th>
                    <th class="py-2 px-3 border-b">Title</th>
                    <th class="py-2 px-3 border-b">Progress</th>
                    <th class="py-2 px-3 border-b">Development points</th>
                    <th class="py-2 px-3 border-b">Status</th>
                    <th class="py-2 px-3 border-b">Completed At</th>
                    <th class="py-2 px-3 border-b">Target Date</th>
                    <th class="py-2 px-3 border-b">Deviation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="goal in data.goals" :key="goal.id"
                   class="border-b border-gray-100 hover:bg-gray-50">
                    <td class="py-2 px-3">{{ goalTypeLabel(goal) }}</td>
                    <td class="py-2 px-3">
                      {{ typeof goal.title === 'object' ? (goal.title.en || Object.values(goal.title)[0]) : (goal.title || '—') }}
                    </td>
                    <td class="py-2 px-3">{{ goal.progress ?? 0 }} / {{ goal.requiredCompletions ?? 1 }}</td>
                    <td class="py-2 px-3">{{ goal.xp ?? 0 }}</td>
                    <td class="py-2 px-3">
                      <span v-if="goal.completed" class="text-green-600 font-medium">
                        <i class="pi pi-check-circle mr-1"></i>Done
                      </span>
                      <span v-else class="text-amber-600">
                        <i class="pi pi-clock mr-1"></i>Pending
                      </span>
                    </td>
                    <td class="py-2 px-3">{{ goal.completedAt ? formatHistoryDate(goal.completedAt) : '—' }}</td>
                    <td class="py-2 px-3">{{ goal.estimatedFinishDate ? formatHistoryDate(goal.estimatedFinishDate) : '—' }}</td>
                    <td class="py-2 px-3">
                      <span v-if="goal.deviationDays == null">—</span>
                      <span v-else-if="goal.isDelayed" class="text-red-600">+{{ goal.deviationDays }} d</span>
                      <span v-else class="text-green-600">{{ goal.deviationDays }} d</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="text-gray-500 text-sm">No goals configured for this level.</div>
            </div>
          </template>
        </DataTable>
        <div v-else-if="userHistory && (!userHistory.levels || userHistory.levels.length === 0)"
         class="text-center text-gray-500 py-6">
          No level progress for this user.
        </div>
      </div>
    </div>

    <!-- Recalculate level state – confirmation dialog -->
    <Dialog
      header="Recalculate level state – confirm changes"
      v-model:visible="recalculateDialogVisible"
      modal
      :closable="true"
      :closeOnEscape="true"
      class="w-full max-w-xl"
      @hide="closeRecalculateDialog"
    >
      <div v-if="recalculatePreviewReport" class="space-y-4">
        <p class="text-sm text-gray-600">
          The following changes would be applied. Confirm to apply them, or cancel to do nothing.
        </p>
        <div v-if="recalculatePreviewReport.isConsistent"
         class="rounded-lg bg-green-50 p-4 text-green-800">
          <strong>Level state is consistent.</strong> No changes needed.
        </div>
        <template v-else>
          <div class="rounded-lg bg-amber-50 p-4 text-amber-800">
            <strong>{{ recalculatePreviewReport.corrections?.length ?? 0 }}
               correction(s)</strong> would be applied.
          </div>
          <div class="text-sm">
            <strong>Resulting current level:</strong> {{ recalculatePreviewReport.currentLevel }}
          </div>
          <div v-if="recalculatePreviewReport.corrections?.length"
           class="border rounded p-3 max-h-48 overflow-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left border-b">
                  <th class="py-1 pr-2">Level</th>
                  <th class="py-1 pr-2">Field</th>
                  <th class="py-1 pr-2">Stored</th>
                  <th class="py-1 pr-2">Computed</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(c, i) in recalculatePreviewReport.corrections" :key="i"
                 class="border-b border-gray-100">
                  <td class="py-1 pr-2">{{ c.level || '—' }}</td>
                  <td class="py-1 pr-2">{{ c.field }}</td>
                  <td class="py-1 pr-2">{{ c.stored }}</td>
                  <td class="py-1 pr-2">{{ c.computed }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
      <template #footer>
        <Button label="Cancel" severity="secondary"
         @click="closeRecalculateDialog" :disabled="recalculateApplying" />
        <Button
          label="Confirm and apply"
          icon="pi pi-check"
          :loading="recalculateApplying"
          :disabled="recalculateApplying ||
           (recalculatePreviewReport && recalculatePreviewReport.isConsistent)"
          @click="confirmRecalculateApply"
        />
      </template>
    </Dialog>

    <!-- Audit Logs Section -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold mb-4">Audit Logs</h3>
      <div class="bg-gray-50 p-4 rounded-lg">
        <div class="flex flex-wrap gap-4 items-end mb-4">
          <div class="flex flex-col">
            <label class="text-sm font-medium text-gray-700 mb-1">User Email</label>
            <InputText
              v-model="auditLogFilters.userEmail"
              placeholder="Filter by email"
              class="w-48"
            />
          </div>
          <div class="flex flex-col">
            <label class="text-sm font-medium text-gray-700 mb-1">Activity Type</label>
            <Dropdown
              v-model="auditLogFilters.activityType"
              :options="auditLogActivityTypes"
              optionLabel="label"
              optionValue="value"
              placeholder="All types"
              class="w-48"
              showClear
            />
          </div>
          <div class="flex flex-col">
            <label class="text-sm font-medium text-gray-700 mb-1">Event Type</label>
            <Dropdown
              v-model="auditLogFilters.eventType"
              :options="auditLogEventTypes"
              optionLabel="label"
              optionValue="value"
              placeholder="All events"
              class="w-40"
              showClear
            />
          </div>
          <div class="flex flex-col">
            <label class="text-sm font-medium text-gray-700 mb-1">Source</label>
            <Dropdown
              v-model="auditLogFilters.source"
              :options="auditLogSourceOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="All sources"
              class="w-40"
              showClear
            />
          </div>
          <div class="flex flex-col">
            <label class="text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <Calendar
              v-model="auditLogFilters.dateRange"
              selectionMode="range"
              dateFormat="yy-mm-dd"
              placeholder="Select date range"
              class="w-56"
              showIcon
              :manualInput="false"
            />
          </div>
          <Button
            label="Apply Filters"
            icon="pi pi-search"
            @click="loadAuditLogs"
            :loading="levelSystemLoading"
          />
          <Button
            :label="auditLogExportWithUserInfo ?
             'Export CSV (with user info)' : 'Export CSV (ID only)'"
            icon="pi pi-download"
            severity="secondary"
            @click="exportAuditLogsClick"
            :loading="levelSystemLoading"
          />
          <div class="flex items-center gap-2">
            <ToggleSwitch v-model="auditLogExportWithUserInfo" />
            <label class="text-sm text-gray-700">Include user details in export</label>
          </div>
        </div>

        <DataTable
          :value="auditLogs"
          :paginator="true"
          :rows="auditLogsLimit"
          :first="(auditLogsPage - 1) * auditLogsLimit"
          :rowsPerPageOptions="[10, 20, 50]"
          :totalRecords="auditLogsTotal"
          :lazy="true"
          :loading="levelSystemLoading"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks
           NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          @page="onAuditLogPage"
          dataKey="_id"
          responsiveLayout="scroll"
        >
          <Column field="timestamp" header="Timestamp" :sortable="false">
            <template #body="{ data }">
              {{ formatAuditTimestamp(data.timestamp) }}
            </template>
          </Column>
          <Column header="User">
            <template #body="{ data }">
              <div v-if="data.userId && typeof data.userId === 'object'"
               class="flex items-center gap-2">
                <img v-if="data.userId.picture_profile?.small"
                     :src="data.userId.picture_profile.small"
                     class="w-7 h-7 rounded-full object-cover"
                     alt="" />
                <div>
                  <div class="font-medium text-sm">{{ data.userId.name }}</div>
                  <div class="text-xs text-gray-500">{{ data.userId.email }}</div>
                </div>
              </div>
              <span v-else class="text-xs text-gray-400">{{ data.userId }}</span>
            </template>
          </Column>
          <Column field="activityType" header="Activity Type" />
          <Column field="eventType" header="Event Type" />
          <Column field="xpValue" header="Development points" />
          <Column field="level" header="Level" />
          <Column field="source" header="Source" />
          <Column field="sourceContext" header="Context" />
          <Column header="Repair Action">
            <template #body="{ data }">
              <span v-if="data.repairAction" class="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                {{ data.repairAction }}
              </span>
              <span v-else class="text-gray-300">—</span>
            </template>
          </Column>
          <Column header="Admin Comment">
            <template #body="{ data }">
              <span v-if="data.adminComment" class="text-sm text-gray-700">{{ data.adminComment }}</span>
              <span v-else class="text-gray-300">—</span>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>

    <!-- Create/Edit Level Dialog -->
    <Dialog :header="levelDialog.level ? 'Edit Level' : 'Create New Level'"
     v-model:visible="levelDialog.visible" modal
      @hide="closeLevelDialog" class="w-3/4">
      <LevelDialog :key="levelDialog.visible ? (levelDialog.level
        ? levelDialog.level._id : 'new') : 'closed'"
        :level="levelDialog.level" :existing-levels="levelTargets"
        :registry-entries="registryEntries"
        :assessment-schemas="assessmentSchemas" @save="handleSaveLevel"
         @cancel="closeLevelDialog" />
    </Dialog>

    <!-- Level Details Dialog -->
    <Dialog :header="`Level ${levelDetailsDialog.level?.level} Details`"
     v-model:visible="levelDetailsDialog.visible"
      modal @hide="closeLevelDetailsDialog" class="w-3/4">
      <div v-if="levelDetailsDialog.level" class="space-y-6">
        <!-- Basic Level Information -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Level Number</label>
            <div class="text-lg font-semibold">{{ levelDetailsDialog.level.level }}</div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <div class="text-lg font-semibold">{{ levelDetailsDialog.level.title }}</div>
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Total development points required</label>
          <div class="text-lg font-semibold text-blue-600">{{
            levelDetailsDialog.level.totalXP }} Development points</div>
        </div>

        <!-- Goals Section -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-3">Goals ({{
            levelDetailsDialog.level.goals.length }})</label>
          <div class="space-y-3">
            <div v-for="(goal, index) in levelDetailsDialog.level.goals" :key="goal.id"
              class="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div class="flex justify-between items-start mb-2">
                <h4 class="font-medium text-gray-900">Goal {{ index + 1 }}</h4>
                <span class="px-2 py-1 text-xs font-medium rounded-full" :class="{
                        'bg-blue-100 text-blue-800': goal.type === 'assessment',
                        'bg-green-100 text-green-800': goal.type === 'devTip',
                        'bg-purple-100 text-purple-800': goal.type === 'travelBuddy'
                      }">
                  {{ goal.type }}
                </span>
              </div>

              <div class="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span class="font-medium text-gray-600">Title (EN):</span>
                  <span class="ml-2 text-gray-900">{{ goal.title?.en || goal.title }}</span>
                </div>
                <div>
                  <span class="font-medium text-gray-600">Title (DE):</span>
                  <span class="ml-2 text-gray-900">{{ goal.title?.de || goal.title }}</span>
                </div>
                <div>
                  <span class="font-medium text-gray-600">Development points:</span>
                  <span class="ml-2 text-gray-900 font-semibold">{{ goal.xp }}
                    Development points</span>
                </div>
                <div>
                  <span class="font-medium text-gray-600">Required Completions:</span>
                  <span class="ml-2 text-gray-900">{{ goal.requiredCompletions }}</span>
                </div>
                <div v-if="goal.type === 'assessment' && goal.assessmentType">
                  <span class="font-medium text-gray-600">Assessment Type:</span>
                  <span class="ml-2 text-gray-900">{{ goal.assessmentType }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Development points summary -->
        <div class="bg-blue-50 p-4 rounded-lg">
          <div class="flex justify-between items-center">
            <span class="font-medium text-blue-800">Goals development points total:</span>
            <span class="text-lg font-bold text-blue-600">
              {{ levelDetailsDialog.level.goals.reduce((sum, goal) => sum + (goal.xp || 0), 0) }}
              Development points
            </span>
          </div>
          <div class="flex justify-between items-center mt-2">
            <span class="font-medium text-blue-800">Level total development points:</span>
            <span class="text-lg font-bold text-blue-600">{{
              levelDetailsDialog.level.totalXP }} Development points</span>
          </div>
          <div class="mt-2 text-sm text-blue-700">
            <span v-if="levelDetailsDialog.level.goals
            .reduce((sum, goal) => sum + (goal.xp || 0), 0) === levelDetailsDialog.level.totalXP">
              ✅ Development points balance is perfect!
            </span>
            <span v-else-if="levelDetailsDialog.level.goals
            .reduce((sum, goal) => sum + (goal.xp || 0), 0) < levelDetailsDialog.level.totalXP">
              ⚠️ Goals development points sum is less than level total development points
            </span>
            <span v-else>
              ❌ Goals development points sum exceeds level total development points
            </span>
          </div>
        </div>
      </div>
    </Dialog>

    <!-- Registry Entry Dialog -->
    <Dialog :header="registryDialog.entry ? 'Edit Registry Entry' : 'Create Registry Entry'"
      v-model:visible="registryDialog.visible" modal @hide="closeRegistryDialog" class="w-1/2">
      <RegistryEntryDialog :entry="registryDialog.entry" @save="handleSaveRegistryEntry"
        @cancel="closeRegistryDialog" />
    </Dialog>

    <!-- Smart Delete Registry Entry Dialog -->
    <Dialog :header="`Delete Registry Entry - ${deleteRegistryDialog.entry?.label || 'Unknown'}`"
      v-model:visible="deleteRegistryDialog.visible" modal
       @hide="closeDeleteRegistryDialog" class="w-1/2">
      <div v-if="deleteRegistryDialog.entry" class="space-y-6">
        <!-- Warning Section -->
        <div class="bg-red-50 border border-red-200 rounded-lg p-4">
          <div class="flex items-center">
            <i class="pi pi-exclamation-triangle text-red-600 text-xl mr-3"></i>
            <div>
              <h4 class="font-semibold text-red-800">⚠️ Registry Entry Deletion Warning</h4>
              <p class="text-red-700 mt-1">
                This action cannot be undone. The registry entry will be permanently removed.
              </p>
            </div>
          </div>
        </div>

        <!-- Entry Details -->
        <div class="bg-gray-50 p-4 rounded-lg">
          <h4 class="font-medium text-gray-700 mb-3">Entry Details</h4>
          <div class="space-y-2">
            <div class="flex justify-between">
              <span class="text-sm text-gray-600">Type:</span>
              <span class="text-sm font-medium">{{ deleteRegistryDialog.entry.type }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-sm text-gray-600">Label:</span>
              <span class="text-sm font-medium">{{ deleteRegistryDialog.entry.label }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-sm text-gray-600">Collection:</span>
              <span class="text-sm font-medium">{{ deleteRegistryDialog.entry.collection }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-sm text-gray-600">Development points per completion:</span>
              <span class="text-sm font-medium">{{
               deleteRegistryDialog.entry.xpPerCompletion }}</span>
            </div>
          </div>
        </div>

        <!-- Confirmation -->
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div class="flex items-center">
            <i class="pi pi-info-circle text-yellow-600 text-xl mr-3"></i>
            <div>
              <p class="text-yellow-800">
                <strong>Confirmation Required:</strong>
                Type <code class="bg-yellow-100 px-2 py-1 rounded">
                  DELETE
                </code> to confirm this action.
              </p>
            </div>
          </div>
          <input
            v-model="deleteRegistryDialog.confirmationText"
            type="text"
            placeholder="Type DELETE to confirm"
            class="mt-3 w-full px-3 py-2 border border-yellow-300
             rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
          />
        </div>
      </div>

      <!-- Footer Actions -->
      <template #footer>
        <div class="flex justify-end space-x-2">
          <Button label="Cancel" severity="secondary" @click="closeDeleteRegistryDialog" />
          <Button
            label="Delete Entry"
            severity="danger"
            :disabled="!canDeleteRegistry"
            @click="confirmDeleteRegistry"
            :loading="deleteRegistryDialog.deleting"
          />
        </div>
      </template>
    </Dialog>

    <!-- Smart Delete Level Dialog -->
    <Dialog :header="`Delete Level
     ${deleteLevelDialog.level?.level} - ${deleteLevelDialog.level?.title}`"
     v-model:visible="deleteLevelDialog.visible" modal
      @hide="closeDeleteLevelDialog" class="w-2/3">
      <div v-if="deleteLevelDialog.level && deleteLevelDialog.assignmentStatus" class="space-y-6">
        <!-- Warning Section -->
        <div class="bg-red-50 border border-red-200 rounded-lg p-4">
          <div class="flex items-center">
            <i class="pi pi-exclamation-triangle text-red-600 text-xl mr-3"></i>
            <div>
              <h4 class="font-semibold text-red-800">⚠️ Level Deletion Warning</h4>
              <p class="text-red-700 mt-1">
                {{ deleteLevelDialog.assignmentStatus.warningMessage
                 || 'This action cannot be undone.' }}
              </p>
            </div>
          </div>
        </div>

        <!-- Assignment Status -->
        <div class="bg-gray-50 p-4 rounded-lg">
          <h4 class="font-medium text-gray-700 mb-3">Current Assignment Status</h4>
          <div class="grid grid-cols-2 gap-4">
            <div class="text-center p-3 bg-white rounded border">
              <div class="text-2xl font-bold text-blue-600">{{
                 deleteLevelDialog.assignmentStatus.assignedUsers }}</div>
              <div class="text-sm text-gray-600">Users Assigned</div>
            </div>
            <div class="text-center p-3 bg-white rounded border">
              <div class="text-2xl font-bold"
               :class="deleteLevelDialog.assignmentStatus.hasUserProgress
               ? 'text-orange-600' : 'text-green-600'">
                {{ deleteLevelDialog.assignmentStatus.hasUserProgress ? 'Yes' : 'No' }}
              </div>
              <div class="text-sm text-gray-600">Has User Progress</div>
            </div>
          </div>
        </div>

        <!-- Delete Options -->
        <div class="space-y-4">
          <h4 class="font-medium text-gray-700">Choose Delete Option:</h4>

          <!-- Soft Delete Option -->
          <div class="border border-gray-200 rounded-lg p-4">
            <div class="flex items-start">
              <input type="radio" id="soft-delete" v-model="deleteLevelDialog.selectedOption"
               value="soft" class="mt-1 mr-3" />
              <div class="flex-1">
                <label for="soft-delete" class="font-medium text-gray-900">
                  🔄 Soft Delete (Recommended)
                </label>
                <p class="text-sm text-gray-600 mt-1">
                  Mark level as deleted and unassign from users,
                   but preserve all user progress and development points data.
                  Users can no longer be assigned to this level,
                   but their achievements remain intact.
                </p>
                <div class="text-xs text-blue-600 mt-2">
                  ✅ Safe option - no data loss
                </div>
              </div>
            </div>
          </div>

          <!-- Cascade Delete Option -->
          <div class="border border-gray-200 rounded-lg p-4">
            <div class="flex items-start">
              <input type="radio" id="cascade-delete" v-model="deleteLevelDialog.selectedOption"
               value="cascade" class="mt-1 mr-3" />
              <div class="flex-1">
                <label for="cascade-delete" class="font-medium text-gray-900">
                  🗑️ Cascade Delete (Dangerous)
                </label>
                <p class="text-sm text-gray-600 mt-1">
                  Completely remove the level and all associated
                   user progress, development points, and completion data.
                  This action is irreversible and will affect {{
                   deleteLevelDialog.assignmentStatus.assignedUsers }} users.
                </p>
                <div class="text-xs text-red-600 mt-2">
                  ⚠️ Data will be permanently lost
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Confirmation -->
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div class="flex items-center">
            <i class="pi pi-info-circle text-yellow-600 text-xl mr-3"></i>
            <div>
              <p class="text-yellow-800">
                <strong>Confirmation Required:</strong>
                Type <code class="bg-yellow-100 px-2 py-1 rounded">
                  DELETE</code> to confirm this action.
              </p>
            </div>
          </div>
          <input
            v-model="deleteLevelDialog.confirmationText"
            type="text"
            placeholder="Type DELETE to confirm"
            class="mt-3 w-full px-3 py-2 border border-yellow-300
             rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
          />
        </div>
      </div>

      <!-- Loading State -->
      <div v-else-if="deleteLevelDialog.loading" class="text-center py-8">
        <i class="pi pi-spin pi-spinner text-2xl text-blue-600"></i>
        <p class="text-gray-600 mt-2">Checking level assignment status...</p>
      </div>

      <!-- Footer Actions -->
      <template #footer>
        <div class="flex justify-end space-x-2">
          <Button label="Cancel" severity="secondary" @click="closeDeleteLevelDialog" />
          <Button
            label="Delete Level"
            severity="danger"
            :disabled="!canDeleteLevel"
            @click="confirmDeleteLevel"
            :loading="deleteLevelDialog.deleting"
          />
        </div>
      </template>
    </Dialog>

    <!-- Anomalies Detector Dialog -->
    <AnomaliesDetector
      v-model:visible="anomaliesDetectorVisible"
      @close="anomaliesDetectorVisible = false"
    />

    <!-- Confirmation Dialog -->
    <ConfirmDialog />

    <!-- Toast for notifications -->
    <Toast />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import ConfirmDialog from 'primevue/confirmdialog';
import Toast from 'primevue/toast';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Calendar from 'primevue/calendar';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import MultiSelect from 'primevue/multiselect';
import ToggleSwitch from 'primevue/toggleswitch';
import LevelDialog from '../components/LevelDialog.vue';
import RegistryEntryDialog from '../components/RegistryEntryDialog.vue';
import AnomaliesDetector from '../components/AnomaliesDetector.vue';
import levelSystemService from '@/services/levelSystemService';

const AUDIT_LOG_ACTIVITY_TYPES = [
  { label: 'Assessment', value: 'assessment' },
  { label: 'Dev Tip', value: 'devTip' },
  { label: 'Travel Buddy', value: 'travelBuddy' },
  { label: 'Create Personal Goal', value: 'createPersonalGoal' },
  { label: 'Achieve Personal Goal', value: 'achievePersonalGoal' },
  { label: 'Set Profile Picture', value: 'setProfilePicture' },
  { label: 'Create Workshop', value: 'createWorkshop' },
  { label: 'Workshop Reflection', value: 'workshopReflection' },
  { label: 'Create Collaboration Tracker', value: 'createCollaborationTracker' },
  { label: 'Level Change', value: 'level_change' },
];
const AUDIT_LOG_EVENT_TYPES = [
  { label: 'Development points award', value: 'xp_award' },
  { label: 'Level Change', value: 'level_change' },
  { label: 'Admin Repair', value: 'admin_repair' },
];
const AUDIT_LOG_SOURCE_OPTIONS = [
  { label: 'All', value: null },
  { label: 'Frontend', value: 'frontend' },
  { label: 'Button', value: 'button' },
  { label: 'API', value: 'api' },
];

export default {
  name: 'LevelSystemManager',
  components: {
    Button,
    Dialog,
    ConfirmDialog,
    Toast,
    DataTable,
    Column,
    Calendar,
    Dropdown,
    InputText,
    InputNumber,
    MultiSelect,
    ToggleSwitch,
    LevelDialog,
    RegistryEntryDialog,
    AnomaliesDetector,
  },
  data() {
    return {
      anomaliesDetectorVisible: false,
      levelSystemChannelOptions: [
        { label: 'Email', value: 'email' },
        { label: 'Push', value: 'push' },
        { label: 'SMS', value: 'sms' },
        { label: 'Popup', value: 'popup' },
      ],
      levelSystemConfig: {
        inactivityThresholdDays: 14,
        inactivityNotificationChannels: ['email', 'push', 'popup', 'sms'],
        stabilisationReminderEnabled: false,
        stabilisationReminderIntervalDays: 7,
      },
      loadingLevelSystemConfig: false,
      levelSystemConfigMessage: null,
      levelSystemConfigSuccess: false,
      loading: {
        registry: false,
        levels: false,
        users: false,
      },
      levelDialog: {
        visible: false,
        level: null,
      },
      levelDetailsDialog: {
        visible: false,
        level: null,
      },
      deleteLevelDialog: {
        visible: false,
        level: null,
        assignmentStatus: null,
        selectedOption: 'soft',
        confirmationText: '',
        loading: false,
        deleting: false,
      },
      registryDialog: {
        visible: false,
        entry: null,
      },
      deleteRegistryDialog: {
        visible: false,
        entry: null,
        loading: false,
        deleting: false,
        confirmationText: '',
      },
      userAssignment: {
        userId: '',
        loading: false,
        message: '',
        success: false,
      },
      auditLogFilters: {
        userEmail: '',
        activityType: null,
        eventType: null,
        source: null,
        dateRange: null,
      },
      auditLogExportWithUserInfo: false,
      auditLogActivityTypes: AUDIT_LOG_ACTIVITY_TYPES,
      auditLogEventTypes: AUDIT_LOG_EVENT_TYPES,
      auditLogSourceOptions: AUDIT_LOG_SOURCE_OPTIONS,
      userHistoryEmail: '',
      userHistoryExportDetailLevel: 'summary',
      userHistoryExportDetailOptions: [
        { label: 'Summary (one row per level)', value: 'summary' },
        { label: 'Per goal (one row per goal)', value: 'goals' },
      ],
      expandedHistoryRows: {},
      recalculateLoading: false,
      recalculatePreviewReport: null,
      recalculateDialogVisible: false,
      recalculateApplying: false,
    };
  },
  computed: {
    userHistoryUserId() {
      const u = this.userHistory?.user;
      if (!u) return null;
      const id = u._id ?? u.id;
      return id != null ? String(id) : null;
    },
    ...mapState('levelSystem', {
      registryEntries: 'registryEntries',
      levelTargets: 'levelTargets',
      userStats: 'userStats',
      userHistory: 'userHistory',
      groupAverage: 'groupAverage',
      auditLogs: 'auditLogs',
      auditLogsTotal: 'auditLogsTotal',
      auditLogsPage: 'auditLogsPage',
      auditLogsLimit: 'auditLogsLimit',
      auditLogsTotalPages: 'auditLogsTotalPages',
      levelSystemLoading: 'loading',
    }),
    ...mapState('assessmentSchemas', [
      'assessmentSchemas',
    ]),

    canDeleteLevel() {
      return this.deleteLevelDialog.confirmationText === 'DELETE' &&
             this.deleteLevelDialog.selectedOption &&
             this.deleteLevelDialog.assignmentStatus;
    },
    canDeleteRegistry() {
      return this.deleteRegistryDialog.confirmationText === 'DELETE';
    },
  },
  async mounted() {
    await this.loadData();
    await this.loadLevelSystemConfig();
    await this.loadAuditLogs(1);
    await this.fetchGroupAverage();
  },
  methods: {
    ...mapActions('levelSystem', [
      'initializeRegistry',
      'fetchRegistryEntries',
      'fetchLevelTargets',
      'fetchUserStats',
      'deleteRegistryEntry',
      'deleteLevelTarget',
      'getLevelAssignmentStatus',
      'createLevelTarget',
      'updateLevelTarget',
      'createRegistryEntry',
      'updateRegistryEntry',
      'assignAllLevelsToUser',
      'fetchAuditLogs',
      'exportAuditLogs',
      'fetchUserHistory',
      'fetchGroupAverage',
      'exportUserHistory',
      'recalculateUserLevelPreview',
      'recalculateUserLevel',
    ]),
    ...mapActions('assessmentSchemas', [
      'fetchAssessments',
    ]),

    async loadData() {
      try {
        await Promise.all([
          this.fetchRegistryEntries(),
          this.fetchLevelTargets(),
          this.fetchUserStats(),
          this.fetchAssessments(),
        ]);
      } catch (error) {
        console.error('Error loading level system data:', error);
      }
    },

    async loadLevelSystemConfig() {
      try {
        const res = await levelSystemService.getLevelSystemConfig();
        const data = res?.data?.data ?? res?.data;
        if (data) {
          this.levelSystemConfig = {
            inactivityThresholdDays: data.inactivityThresholdDays ?? 14,
            inactivityNotificationChannels: Array.isArray(data.inactivityNotificationChannels)
              ? data.inactivityNotificationChannels
              : ['email', 'push', 'popup', 'sms'],
            stabilisationReminderEnabled: data.stabilisationReminderEnabled ?? false,
            stabilisationReminderIntervalDays: data.stabilisationReminderIntervalDays ?? 7,
          };
        }
      } catch (error) {
        console.error('Error loading level system config:', error);
      }
    },

    async saveLevelSystemConfig() {
      this.levelSystemConfigMessage = null;
      this.loadingLevelSystemConfig = true;
      try {
        await levelSystemService.updateLevelSystemConfig(this.levelSystemConfig);
        this.levelSystemConfigSuccess = true;
        this.levelSystemConfigMessage = 'Level system configuration saved successfully.';
      } catch (error) {
        this.levelSystemConfigSuccess = false;
        this.levelSystemConfigMessage = error?.response?.data?.message || error?.message || 'Failed to save configuration.';
      } finally {
        this.loadingLevelSystemConfig = false;
      }
    },

    async refreshUserStats() {
      try {
        this.loading.users = true;
        await this.fetchUserStats();
      } catch (error) {
        console.error('Error refreshing user stats:', error);
      } finally {
        this.loading.users = false;
      }
    },

    formatTimeAgo(timestamp) {
      if (!timestamp) return 'Unknown';

      const now = new Date();
      const time = new Date(timestamp);
      const diffInSeconds = Math.floor((now - time) / 1000);

      if (diffInSeconds < 60) return 'Just now';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
      if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;

      return time.toLocaleDateString();
    },

    formatAuditTimestamp(timestamp) {
      if (!timestamp) return '—';
      const d = new Date(timestamp);
      return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    },

    formatHistoryDate(value) {
      if (!value) return '—';
      const d = value instanceof Date ? value : new Date(value);
      return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    },

    goalTypeLabel(goal) {
      const labels = {
        assessment: 'Assessment',
        devTip: 'Dev Tip',
        travelBuddy: 'Travel Buddy',
        createPersonalGoal: 'Create Personal Goal',
        achievePersonalGoal: 'Achieve Personal Goal',
        setProfilePicture: 'Set Profile Picture',
        createWorkshop: 'Create Workshop',
        workshopReflection: 'Workshop Reflection',
        createCollaborationTracker: 'Collaboration Tracker',
      };
      const base = labels[goal.type] || goal.type;
      if (goal.type === 'assessment' && goal.assessmentType) {
        return `${base} (${goal.assessmentType})`;
      }
      return base;
    },

    async loadUserHistory() {
      const email = this.userHistoryEmail?.trim();
      if (!email) return;
      await this.fetchUserHistory(email);
    },

    async exportUserHistoryClick() {
      const params = { detailLevel: this.userHistoryExportDetailLevel };
      if (this.userHistoryEmail?.trim()) {
        params.userEmail = this.userHistoryEmail.trim();
      }
      await this.exportUserHistory(params);
    },

    async recalculateLevelStateClick() {
      const userId = this.userHistoryUserId;
      if (!userId) return;
      this.recalculateLoading = true;
      this.recalculatePreviewReport = null;
      try {
        const result = await this.recalculateUserLevelPreview(userId);
        const data = result?.data ?? result;
        this.recalculatePreviewReport = data;
        this.recalculateDialogVisible = true;
      } catch (err) {
        const detail = err?.response?.data?.message || err?.message || 'Preview failed';
        this.$store.dispatch('notifications/showToast', {
          severity: 'error',
          summary: 'Recalculate preview',
          detail,
          life: 5000,
        }, { root: true });
      } finally {
        this.recalculateLoading = false;
      }
    },

    closeRecalculateDialog() {
      this.recalculateDialogVisible = false;
      this.recalculatePreviewReport = null;
    },

    async confirmRecalculateApply() {
      const userId = this.userHistoryUserId;
      if (!userId || !this.recalculatePreviewReport) return;
      this.recalculateApplying = true;
      try {
        const result = await this.recalculateUserLevel(userId);
        const data = result?.data ?? result;
        const n = data?.corrections?.length ?? 0;
        const msg = data?.isConsistent
          ? 'Level state was already consistent. No changes applied.'
          : `Applied ${n} correction(s). Current level: ${data?.currentLevel ?? '—'}.`;
        this.$store.dispatch('notifications/showToast', {
          severity: 'success',
          summary: 'Recalculate applied',
          detail: msg,
          life: 5000,
        }, { root: true });
        this.closeRecalculateDialog();
        if (this.userHistoryEmail?.trim()) {
          await this.fetchUserHistory(this.userHistoryEmail.trim());
        }
      } catch (err) {
        const detail = err?.response?.data?.message || err?.message || 'Apply failed';
        this.$store.dispatch('notifications/showToast', {
          severity: 'error',
          summary: 'Recalculate apply',
          detail,
          life: 5000,
        }, { root: true });
      } finally {
        this.recalculateApplying = false;
      }
    },

    async loadAuditLogs(page = 1) {
      const filters = {};
      if (this.auditLogFilters.userEmail) {
        filters.userEmail = this.auditLogFilters.userEmail;
      }
      if (this.auditLogFilters.activityType) {
        filters.activityType = this.auditLogFilters.activityType;
      }
      if (this.auditLogFilters.eventType) {
        filters.eventType = this.auditLogFilters.eventType;
      }
      if (this.auditLogFilters.source) {
        filters.source = this.auditLogFilters.source;
      }
      if (this.auditLogFilters.dateRange && this.auditLogFilters.dateRange.length === 2) {
        const [start, end] = this.auditLogFilters.dateRange;
        filters.startDate = start;
        filters.endDate = end;
      }
      await this.fetchAuditLogs({
        filters,
        page,
        limit: this.auditLogsLimit,
      });
    },

    onAuditLogPage(event) {
      const page = (event.page ?? 0) + 1;
      const limit = event.rows ?? this.auditLogsLimit;
      const filters = {};
      if (this.auditLogFilters.userEmail) {
        filters.userEmail = this.auditLogFilters.userEmail;
      }
      if (this.auditLogFilters.activityType) {
        filters.activityType = this.auditLogFilters.activityType;
      }
      if (this.auditLogFilters.eventType) {
        filters.eventType = this.auditLogFilters.eventType;
      }
      if (this.auditLogFilters.source) {
        filters.source = this.auditLogFilters.source;
      }
      if (this.auditLogFilters.dateRange && this.auditLogFilters.dateRange.length === 2) {
        const [start, end] = this.auditLogFilters.dateRange;
        filters.startDate = start;
        filters.endDate = end;
      }
      this.fetchAuditLogs({ filters, page, limit });
    },

    async exportAuditLogsClick() {
      const filters = {};
      if (this.auditLogFilters.userEmail) {
        filters.userEmail = this.auditLogFilters.userEmail;
      }
      if (this.auditLogFilters.activityType) {
        filters.activityType = this.auditLogFilters.activityType;
      }
      if (this.auditLogFilters.eventType) {
        filters.eventType = this.auditLogFilters.eventType;
      }
      if (this.auditLogFilters.source) {
        filters.source = this.auditLogFilters.source;
      }
      if (this.auditLogFilters.dateRange && this.auditLogFilters.dateRange.length === 2) {
        const [start, end] = this.auditLogFilters.dateRange;
        filters.startDate = start;
        filters.endDate = end;
      }
      filters.includeUserDetails = this.auditLogExportWithUserInfo;
      await this.exportAuditLogs(filters);
    },

    openCreateLevelDialog() {
      this.levelDialog.level = null;
      this.levelDialog.visible = true;
    },

    openCreateRegistryDialog() {
      this.registryDialog.entry = null;
      this.registryDialog.visible = true;
    },

    editLevel(level) {
      this.levelDialog.level = { ...level };
      this.levelDialog.visible = true;
    },

    viewLevelDetails(level) {
      // Show level details in a modal
      this.levelDetailsDialog.level = { ...level };
      this.levelDetailsDialog.visible = true;
    },

    closeLevelDialog() {
      this.levelDialog.visible = false;
      this.levelDialog.level = null;
    },

    closeLevelDetailsDialog() {
      this.levelDetailsDialog.visible = false;
      this.levelDetailsDialog.level = null;
    },

    async showDeleteLevelDialog(level) {
      this.deleteLevelDialog.level = { ...level };
      this.deleteLevelDialog.visible = true;
      this.deleteLevelDialog.loading = true;
      this.deleteLevelDialog.selectedOption = 'soft';
      this.deleteLevelDialog.confirmationText = '';

      try {
        // Get level assignment status
        const status = await this.getLevelAssignmentStatus(level._id);
        this.deleteLevelDialog.assignmentStatus = status;
      } catch (error) {
        console.error('Error getting level assignment status:', error);
        // Show error toast
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to get level assignment status',
          life: 3000,
        });
      } finally {
        this.deleteLevelDialog.loading = false;
      }
    },

    closeDeleteLevelDialog() {
      this.deleteLevelDialog.visible = false;
      this.deleteLevelDialog.level = null;
      this.deleteLevelDialog.assignmentStatus = null;
      this.deleteLevelDialog.selectedOption = 'soft';
      this.deleteLevelDialog.confirmationText = '';
      this.deleteLevelDialog.loading = false;
      this.deleteLevelDialog.deleting = false;
    },

    async confirmDeleteLevel() {
      if (this.deleteLevelDialog.confirmationText !== 'DELETE') {
        this.$toast.add({
          severity: 'warn',
          summary: 'Warning',
          detail: 'Please type DELETE to confirm',
          life: 3000,
        });
        return;
      }

      this.deleteLevelDialog.deleting = true;

      try {
        const result = await this.deleteLevelTarget({
          levelId: this.deleteLevelDialog.level._id,
          deleteOption: this.deleteLevelDialog.selectedOption,
        });

        if (result.success) {
          this.$toast.add({
            severity: 'success',
            summary: 'Success',
            detail: result.message,
            life: 5000,
          });

          // Refresh data
          await this.loadData();
          this.closeDeleteLevelDialog();
        }
      } catch (error) {
        console.error('Error deleting level:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete level',
          life: 3000,
        });
      } finally {
        this.deleteLevelDialog.deleting = false;
      }
    },

    async handleSaveLevel(levelData) {
      try {
        // Clean the data before sending to backend
        const cleanData = { ...levelData };

        // Remove MongoDB-specific properties that shouldn't be sent
        delete cleanData.__v;
        delete cleanData.createdAt;
        delete cleanData.updatedAt;

        if (levelData._id) {
          // For updates, also remove _id from the data object (it's passed separately)
          const { _id, ...updateData } = cleanData;
          await this.updateLevelTarget({ id: _id, levelData: updateData });
        } else {
          // For creates, remove _id if it exists
          delete cleanData._id;
          await this.createLevelTarget(cleanData);
        }

        await this.loadData();
        this.closeLevelDialog();

        // Show success message
        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: levelData._id ? 'Level updated successfully!' : 'Level created successfully!',
          life: 3000,
        });
      } catch (error) {
        console.error('Error saving level:', error);

        // Show error message
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.response?.data?.message || error.message || 'Failed to save level',
          life: 5000,
        });
      }
    },

    editRegistryEntry(entry) {
      this.registryDialog.entry = { ...entry };
      this.registryDialog.visible = true;
    },

    closeRegistryDialog() {
      this.registryDialog.visible = false;
      this.registryDialog.entry = null;
    },

    showDeleteRegistryDialog(entry) {
      this.deleteRegistryDialog.entry = { ...entry };
      this.deleteRegistryDialog.visible = true;
      this.deleteRegistryDialog.confirmationText = '';
    },

    closeDeleteRegistryDialog() {
      this.deleteRegistryDialog.visible = false;
      this.deleteRegistryDialog.entry = null;
      this.deleteRegistryDialog.confirmationText = '';
      this.deleteRegistryDialog.loading = false;
      this.deleteRegistryDialog.deleting = false;
    },

    // Deep clean: remove _id properties from nested objects only (preserve top-level _id)
    deepRemoveId(obj) {
      if (obj === null || typeof obj !== 'object') {
        return obj;
      }

      if (Array.isArray(obj)) {
        return obj.map((item) => this.deepRemoveId(item));
      }

      const cleaned = {};
      Object.entries(obj).forEach(([key, value]) => {
        if (key === '_id') {
          // Preserve top-level _id, only clean nested objects
          cleaned[key] = value;
        } else if (typeof value === 'object' && value !== null) {
          // Recursively clean nested objects (this will remove nested _id fields)
          cleaned[key] = this.deepRemoveId(value);
        } else {
          // Keep primitive values as-is
          cleaned[key] = value;
        }
      });
      return cleaned;
    },

    async confirmDeleteRegistry() {
      if (this.deleteRegistryDialog.confirmationText !== 'DELETE') {
        this.$toast.add({
          severity: 'warn',
          summary: 'Warning',
          detail: 'Please type DELETE to confirm',
          life: 3000,
        });
        return;
      }

      this.deleteRegistryDialog.deleting = true;

      try {
        await this.deleteRegistryEntry(this.deleteRegistryDialog.entry._id);
        await this.loadData();
        this.closeDeleteRegistryDialog();
      } catch (error) {
        console.error('Error deleting registry entry:', error);
        // Error toast is now handled in the store
      } finally {
        this.deleteRegistryDialog.deleting = false;
      }
    },

    async handleSaveRegistryEntry(entryData) {
      try {
        // Clean the data before sending to backend
        const cleanData = { ...entryData };

        // Remove MongoDB-specific properties that shouldn't be sent
        delete cleanData.__v;
        delete cleanData.createdAt;
        delete cleanData.updatedAt;

        // Create clean object with only the values we need (no _id fields)
        const cleanDataToSend = {
          type: cleanData.type,
          label: cleanData.label,
          description: cleanData.description,
          xpPerCompletion: cleanData.xpPerCompletion,
          collection: cleanData.collection,
          userField: cleanData.userField,
          isDynamic: cleanData.isDynamic,
          validation: {
            requiresSchemaFlag: cleanData.validation.requiresSchemaFlag,
            minXpPerCompletion: cleanData.validation.minXpPerCompletion,
          },
          goalType: cleanData.goalType,
          requiresAssessmentType: cleanData.requiresAssessmentType,
        };

        if (entryData._id) {
          // For updates, use the clean data without _id
          await this.updateRegistryEntry({
            id: entryData._id,
            entryData: cleanDataToSend,
          });
        } else {
          // For creates, use the clean data (no _id to remove)
          await this.createRegistryEntry(cleanDataToSend);
        }

        await this.loadData();
        this.closeRegistryDialog();
      } catch (error) {
        console.error('Error saving registry entry:', error);
        // Error toast is now handled in the store
      }
    },

    async assignAllLevelsToUser() {
      if (!this.userAssignment.userId.trim()) {
        this.userAssignment.message = 'Please enter a valid user ID';
        this.userAssignment.success = false;
        return;
      }

      this.userAssignment.loading = true;
      this.userAssignment.message = '';

      try {
        // Call the store action to assign all levels to the user
        const result = await this.$store.dispatch('levelSystem/assignAllLevelsToUser', {
          userId: this.userAssignment.userId.trim(),
        });

        if (result.success) {
          this.userAssignment.message = `Successfully assigned all ${this.levelTargets.length}
            levels to user ${this.userAssignment.userId}`;
          this.userAssignment.success = true;
          this.userAssignment.userId = ''; // Clear the input

          // Show success toast
          this.$toast.add({
            severity: 'success',
            summary: 'Success',
            detail: this.userAssignment.message,
            life: 5000,
          });

          // Refresh user stats to reflect the changes
          await this.refreshUserStats();
        } else {
          this.userAssignment.message = result.message || 'Failed to assign levels to user';
          this.userAssignment.success = false;
        }
      } catch (error) {
        console.error('Error assigning levels to user:', error);
        this.userAssignment.message = error.response?.data?.message || error.message || 'Failed to assign levels to user';
        this.userAssignment.success = false;

        // Show error toast
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: this.userAssignment.message,
          life: 5000,
        });
      } finally {
        this.userAssignment.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Component-specific styles can be added here */
</style>
